import React from 'react'
import { Form } from 'react-bootstrap'
import { formClasses } from '../../../../sharedLayout/forms'
import { bmiBlockClasses } from '../../../../../utils/style'
import { getClassName } from '../../../../../utils/utils'
import { BmiBlock } from '../../blocksState'
import { BMIInputLayout, QuestionLayout } from './bmiBlockUtils'
import i18next from 'i18next'
import { useInView } from '../../../../hooks/useInView'

export type BmiBlockStyle = Partial<{
  blockClassName: string
  inviewClassName: string
  innerClassName: string
  headerClassName: string
  titleClassName: string
  contentClassName: string
  calculatorClassName: string
  readOnlyBMIClassName: string
  listClassName: string
  itemClassName: string
  resultsClassName: string
}>

//This type contains all props the BMI block needs: The variables and the functions to change them.
export type BmiBlockProps = {
  age?: number
  length?: number
  weight?: number
  gastricBand?: boolean

  bmi?: number
  overweight?: number

  setBool: (_: boolean, prop: string, depth: number) => void
  setAge: (_: number) => void
  setLength: (_: number) => void
  setWeight: (_: number) => void
}

const roundNum = (num: number): number => Math.round(num*10)/10 // Rounds number to 1 or 0 decimal numbers

const escapeHtmlExceptStrong = (input: string): string => {
  let escapedHtml = input
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')

  escapedHtml = escapedHtml
      .replace(/&lt;strong&gt;/g, '<strong>')
      .replace(/&lt;\/strong&gt;/g, '</strong>')

  return escapedHtml;
}

export function BmiBlockLayout(props: BmiBlockProps & BmiBlock) {
  const {
    index,
    inview,
    age,
    length,
    weight,
    gastricBand,
    setAge: setAge,
    setLength: setLength,
    setWeight: setWeight,
    setBool: setBool,
    bmi,
    overweight,
  } = props
  //Response A - The patient is too young for an operation
  const responseA = i18next.t("bmi:TooYoungResponse")
  //Response C - The patient may get an operation
  const responseC = i18next.t("bmi:EligibleOperationResponse")
  //Response D - The patient may not get an operation
  const responseD = i18next.t("bmi:NotEligibleOperationResponse")
  const isOverweight = overweight ? overweight > 0 : false
  const bmiResultString = bmi ? bmi.toFixed(1).replace('.', ',') : undefined
  const bmiRounded = bmi ? roundNum(bmi) : undefined

  const overweightResults = length && weight ?  {
    minHealthy: (0.4*length).toFixed(0),
    maxHealthy: (0.5*length).toFixed(0),
    minResult: (0.6*weight).toFixed(0),
    maxResult: (0.8*weight).toFixed(0),
  } : undefined

  const overweightString: string = i18next.t("bmi:overweightResponse",
  {
    minHealthy: overweightResults?.minHealthy,
    maxHealthy: overweightResults?.maxHealthy,
    minResult: overweightResults?.minResult,
    maxResult: overweightResults?.maxResult,
    interpolation: { escapeValue: false }
  })

  const escapedOverweightString = escapeHtmlExceptStrong(overweightString)

  const { inView, ref } = useInView(
    { threshold: 0.1 },
    (inview) => console.debug("BmiBlockLayout inView:", inview),
    !inview?.triggerOnce
  )

  return (
    <section
      key={`block-${index}`}
      id={`io-${index}`}
      ref={ref}
      className={getClassName(bmiBlockClasses, 'blockClassName', 'container') + ' ' + (inView ? getClassName(bmiBlockClasses, 'inviewClassName', 'inview') : '')}
    >
      <div className={getClassName(bmiBlockClasses, 'innerClassName', 'py-5')}>
        <header className={getClassName(bmiBlockClasses, 'headerClassName', '')}>
          <h2 className={getClassName(bmiBlockClasses, 'titleClassName', 'mb-3')}>Bereken uw Body Mass Index</h2>
        </header>

        {/* Input volgorde -> Leeftijd, Lengte (cm), Gewicht (kg) */}
        {/* Formula for calculating the BMI -> (Weight / Height) / Height -> BMI [Weight is kg, Height is cm]*/}
        <div className={getClassName(bmiBlockClasses, 'contentClassName', '')}>
          {/* ----- Calculator */}
          <fieldset>
            <legend className="sr-text">Voer uw leeftijd, lengte en gewicht in</legend>
            <div className={getClassName(bmiBlockClasses, 'calculatorClassName', '')}>
              <BMIInputLayout
                id="bmiInputAge"
                label="Leeftijd"
                value={age}
                onKeyPress={(e: any) => {
                  !/[0-9]/.test(e.key) ? e.preventDefault() : null
                }}
                onChange={(e) => setAge(parseInt(e.currentTarget.value))}
                extraSpan="jaar"
              />
              <BMIInputLayout
                id="bmiInputHeight"
                label="Lengte"
                value={length}
                onKeyPress={(e: any) => {
                  !/[0-9]/.test(e.key) ? e.preventDefault() : null
                }}
                onChange={(e) => setLength(parseInt(e.currentTarget.value))}
                extraSpan={<abbr title="Centimeter">cm</abbr>}
              />
              <BMIInputLayout
                id="bmiInputWeight"
                label="Gewicht"
                value={weight}
                onKeyPress={(e: any) => {
                  !/[0-9]/.test(e.key) ? e.preventDefault() : null
                }}
                onChange={(e) => setWeight(parseInt(e.currentTarget.value))}
                extraSpan={<abbr title="Kilogram">kg</abbr>}
              />

              <Form.Group className={getClassName(formClasses, 'formGroupClassName', '')}>
                {age && weight && length ? (
                  <>
                    <Form.Label className={getClassName(formClasses, 'formLabelClassName', '')}>
                      <abbr title="Body Mass Index" lang="en">
                        BMI
                      </abbr>
                    </Form.Label>
                    <Form.Control
                      id="bmiReadonly"
                      readOnly={true}
                      className={getClassName(bmiBlockClasses, 'readOnlyBMIClassName', '')}
                      value={bmiResultString}
                    />
                  </>
                ) : (
                  <></>
                )}
              </Form.Group>
            </div>
          </fieldset>

          {/* ----- Questions */}

          <ul className={getClassName(bmiBlockClasses, 'listClassName', '')}>
            {(age && weight && length && bmi && bmiRounded ? (bmi < 35 && bmiRounded < 35) : false) ? (
              <QuestionLayout
                id="bmi-gastricBand"
                question="Hebt u een maagband en ondervindt u daar klachten of problemen van?"
                option1={{ checked: gastricBand == true, onChange: () => setBool(true, 'gastricBand', 1) }}
                option2={{ checked: gastricBand == false, onChange: () => setBool(false, 'gastricBand', 1) }}
                style={bmiBlockClasses}
              />
            ) : undefined}
          </ul>

          {/* ----- Results */}
          {age &&
          length &&
          weight &&
          bmi &&
          bmiRounded &&
          (age < 18 ||
            (bmi < 35 && gastricBand != undefined) ||
            (bmi >= 35 ||
              bmiRounded >= 35)) ? (
            <div className={getClassName(bmiBlockClasses, 'resultsClassName', 'bg-primary')}>
              {age && length && weight ? (
                age < 18 ? (
                  <p>{responseA}</p>
                ) : bmi >= 35 || bmiRounded >= 35 ? (
                  <p>{responseC}</p>
                ) : gastricBand != undefined ? (
                  gastricBand == true ? (
                    <p>{responseC}</p>
                  ) : (
                    <p>{responseD}</p>
                  )
                ) : (
                  <></>
                )
              ) : (
                <></>
              )}

              {
                <>
                  <p>
                    <span>
                      Uw{' '}
                      <abbr title="Body Mass Index" lang="en">
                        BMI
                      </abbr>{' '}
                      is <b>{bmiResultString}</b>.
                    </span>{' '}
                    {isOverweight ? (
                      <>
                      <span>
                        Uw overgewicht is <b>{overweight?.toFixed(1).replace('.', ',')} kg</b>.
                      </span>

                      </>
                    ) : (
                      <span>
                        U heeft <b>geen</b> overgewicht.
                      </span>
                    )}
                  </p>
                  { bmi >= 35 ? (
                    <p dangerouslySetInnerHTML={{__html: escapedOverweightString}}></p>
                    ) : (<></>)
                  }
                </>
              }
            </div>
          ) : (
            <></>
          )}
        </div>
      </div>
    </section>
  )
}
